import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import styles from './Stepper.module.css';
import { MinusIcon, PlusIcon } from '@/components/icons';

interface StepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  /** Accessible name — the control has no visible label of its own. */
  label: string;
  /** Explanatory line under the value, e.g. "Começa no par · +2 neste buraco". */
  hint?: ReactNode;
}

export function Stepper({
  value,
  onChange,
  min = 0,
  max = 99,
  size = 'md',
  label,
  hint,
}: StepperProps) {
  const canDec = value > min;
  const canInc = value < max;

  return (
    <div>
      <div className={cx(styles.wrap, styles[size])}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => canDec && onChange(value - 1)}
          disabled={!canDec}
          aria-label={t('a11y.decrease', { label })}
        >
          <MinusIcon size={18} />
        </button>
        <output className={styles.value} aria-label={`${label}: ${value}`}>
          {value}
        </output>
        <button
          type="button"
          className={cx(styles.btn, styles.inc)}
          onClick={() => canInc && onChange(value + 1)}
          disabled={!canInc}
          aria-label={t('a11y.increase', { label })}
        >
          <PlusIcon size={18} />
        </button>
      </div>
      {hint != null && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}
